import React, { useEffect, useMemo, useState } from "react";
import { BarChart3, Download } from "lucide-react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";

import Layout from "src/components/layout/dashboard/Layout";
import PageHeader from "src/components/common/PageHeader";
import Card from "src/components/common/card/Card";
import Button from "src/components/ui/Button";
import Input from "src/components/ui/Input";
import Dropdown from "src/components/ui/Dropdown";
import FilterSelect from "src/components/ui/FilterSelect";
import TableV2 from "src/components/ui/TableV2";
import { Badge } from "src/components/ui/Badge";

import adminReportsApis from "src/api/adminReports";
import { AdminReportResult, AdminReportType } from "src/types/adminReports.types";
import { currencyFormatter } from "src/utils/common";
import { formatDateTime } from "src/utils/date-time";

const REPORT_TYPE_OPTIONS: { label: string; value: AdminReportType }[] = [
  { label: "Tenants", value: "tenants" },
  { label: "Sign-up Requests", value: "signups" },
  { label: "Transactions", value: "transactions" },
  { label: "Support Tickets", value: "support" },
];

const STATUS_OPTIONS: Record<AdminReportType, { label: string; value: string }[]> = {
  tenants: [
    { label: "Active", value: "active" },
    { label: "Suspended", value: "suspended" },
    { label: "Draft", value: "draft" },
  ],
  signups: [
    { label: "Pending", value: "pending_review" },
    { label: "Approved", value: "approved" },
    { label: "Rejected", value: "rejected" },
  ],
  transactions: [
    { label: "Success", value: "success" },
    { label: "Failed", value: "failed" },
    { label: "Processing", value: "processing" },
    { label: "Created", value: "created" },
  ],
  support: [
    { label: "Open", value: "OPEN" },
    { label: "Closed", value: "CLOSED" },
  ],
};

const statusColor = (status: string) => {
  const s = status?.toLowerCase();
  if (["active", "approved", "success", "closed"].includes(s)) return "green";
  if (["suspended", "rejected", "failed"].includes(s)) return "red";
  return "yellow";
};

const SUMMARY_CARDS: Record<AdminReportType, { key: string; label: string; format?: "currency" | "hours" }[]> = {
  tenants: [
    { key: "total", label: "Total Tenants" },
    { key: "active", label: "Active" },
    { key: "suspended", label: "Suspended" },
    { key: "draft", label: "Draft" },
  ],
  signups: [
    { key: "total", label: "Total Requests" },
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
  ],
  transactions: [
    { key: "total", label: "Total Transactions" },
    { key: "success", label: "Successful" },
    { key: "failed", label: "Failed" },
    { key: "totalRevenue", label: "Revenue", format: "currency" },
  ],
  support: [
    { key: "total", label: "Total Tickets" },
    { key: "open", label: "Open" },
    { key: "closed", label: "Closed" },
    { key: "avgResolutionHours", label: "Avg. Resolution", format: "hours" },
  ],
};

const REPORT_COLUMNS: Record<AdminReportType, any[]> = {
  tenants: [
    { key: "tenantId", header: "Tenant ID" },
    { key: "tenantName", header: "Name" },
    { key: "tenantIdentifier", header: "Identifier" },
    { key: "tenantEmail", header: "Email" },
    { key: "tenantMobile", header: "Mobile" },
    { key: "plan", header: "Plan" },
    {
      key: "tenantStatus",
      header: "Status",
      accessor: ({ row }: any) => (
        <Badge text={(row.tenantStatus || "").toUpperCase()} color={statusColor(row.tenantStatus)} />
      ),
    },
    {
      key: "createdAt",
      header: "Created",
      accessor: ({ row }: any) => formatDateTime(row.createdAt, "D MMM YYYY"),
    },
  ],
  signups: [
    { key: "requestId", header: "Request ID" },
    { key: "organizationName", header: "Organization" },
    { key: "organizationIdentifier", header: "Identifier" },
    { key: "ownerName", header: "Owner" },
    { key: "email", header: "Email" },
    { key: "mobile", header: "Mobile" },
    {
      key: "status",
      header: "Status",
      accessor: ({ row }: any) => (
        <Badge
          text={row.status?.replace("_", " ").toUpperCase()}
          color={statusColor(row.status)}
        />
      ),
    },
    {
      key: "createdAt",
      header: "Submitted",
      accessor: ({ row }: any) => formatDateTime(row.createdAt, "D MMM YYYY"),
    },
    {
      key: "reviewedAt",
      header: "Reviewed",
      accessor: ({ row }: any) =>
        row.reviewedAt ? formatDateTime(row.reviewedAt, "D MMM YYYY") : "—",
    },
  ],
  transactions: [
    { key: "transactionId", header: "Transaction ID" },
    {
      key: "amount",
      header: "Amount",
      accessor: ({ row }: any) => currencyFormatter(row.amount || 0),
    },
    { key: "transactionType", header: "Type" },
    {
      key: "status",
      header: "Status",
      accessor: ({ row }: any) => (
        <Badge text={(row.status || "").toUpperCase()} color={statusColor(row.status)} />
      ),
    },
    { key: "paymentMethod", header: "Method" },
    { key: "referenceId", header: "Reference" },
    {
      key: "createdAt",
      header: "Created",
      accessor: ({ row }: any) => formatDateTime(row.createdAt, "D MMM YYYY, HH:mm"),
    },
  ],
  support: [
    { key: "requestId", header: "Request ID" },
    { key: "name", header: "Name" },
    { key: "email", header: "Email" },
    { key: "source", header: "Source" },
    {
      key: "status",
      header: "Status",
      accessor: ({ row }: any) => (
        <Badge text={(row.status || "").toUpperCase()} color={statusColor(row.status)} />
      ),
    },
    { key: "repliesCount", header: "Replies" },
    {
      key: "createdAt",
      header: "Created",
      accessor: ({ row }: any) => formatDateTime(row.createdAt, "D MMM YYYY"),
    },
  ],
};

const toExportRow = (type: AdminReportType, row: any): Record<string, any> => {
  switch (type) {
    case "tenants":
      return {
        "Tenant ID": row.tenantId,
        Name: row.tenantName,
        Identifier: row.tenantIdentifier,
        Email: row.tenantEmail,
        Mobile: row.tenantMobile,
        Plan: row.plan,
        Status: row.tenantStatus,
        Created: row.createdAt,
      };
    case "signups":
      return {
        "Request ID": row.requestId,
        Organization: row.organizationName,
        Identifier: row.organizationIdentifier,
        Owner: row.ownerName,
        Email: row.email,
        Mobile: row.mobile,
        Status: row.status,
        Submitted: row.createdAt,
        Reviewed: row.reviewedAt || "",
      };
    case "transactions":
      return {
        "Transaction ID": row.transactionId,
        Amount: row.amount,
        Type: row.transactionType,
        Status: row.status,
        Method: row.paymentMethod,
        Mode: row.paymentMode,
        Reference: row.referenceId,
        Remarks: row.remarks,
        Created: row.createdAt,
      };
    case "support":
      return {
        "Request ID": row.requestId,
        Name: row.name,
        Email: row.email,
        Source: row.source,
        Status: row.status,
        Replies: row.repliesCount,
        Created: row.createdAt,
        Updated: row.updatedAt,
      };
    default:
      return row;
  }
};

const AdminReportsPage: React.FC = () => {
  const [reportType, setReportType] = useState<AdminReportType>("tenants");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [exportFormat, setExportFormat] = useState<"csv" | "excel">("csv");
  const [exporting, setExporting] = useState(false);
  const [hasRun, setHasRun] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AdminReportResult | null>(null);

  // Switching report type starts fresh — a status value from one type's
  // enum isn't meaningful for another.
  useEffect(() => {
    setStatus("");
  }, [reportType]);

  const currentParams = useMemo(
    () => ({
      type: reportType,
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      ...(status ? { status } : {}),
      ...(search ? { search } : {}),
    }),
    [reportType, dateFrom, dateTo, status, search]
  );

  const runReport = async (nextPage = 1) => {
    setHasRun(true);
    setLoading(true);
    try {
      const res = (await adminReportsApis.runReport({
        ...currentParams,
        page: nextPage,
        limit: 10,
      })) as AdminReportResult;
      setResult(res);
    } catch (error) {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const res = (await adminReportsApis.runReport({
        ...currentParams,
        limit: "all",
      })) as AdminReportResult;
      const rows = res?.data || [];
      if (!rows.length) {
        toast.info("No rows to export for the current filters");
        return;
      }
      const exportRows = rows.map((row) => toExportRow(reportType, row));
      const worksheet = XLSX.utils.json_to_sheet(exportRows);

      if (exportFormat === "csv") {
        const csv = XLSX.utils.sheet_to_csv(worksheet);
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${reportType}-report.csv`;
        link.click();
        URL.revokeObjectURL(url);
      } else {
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Report");
        XLSX.writeFile(workbook, `${reportType}-report.xlsx`);
      }
    } catch (error) {
      toast.error("Failed to export report");
    } finally {
      setExporting(false);
    }
  };

  const summary = result?.summary;
  const rows = result?.data || [];
  const pagination = result?.pagination;
  const columns = REPORT_COLUMNS[reportType];

  const formatSummaryValue = (
    value: number | null | undefined,
    format?: "currency" | "hours"
  ) => {
    if (value === null || value === undefined) return "N/A";
    if (format === "currency") return currencyFormatter(value);
    if (format === "hours") return `${value}h`;
    return value.toLocaleString();
  };

  return (
    <Layout
      type="admin"
      meta={{
        title: "Reports | Invyntra Admin",
        description: "Tenants, sign-ups, revenue, and support — exportable.",
        keywords: "reports, admin, analytics, invyntra",
      }}
    >
      <div className="space-y-4">
        <PageHeader
          title="Reports"
          breadcrumbData={[
            { label: "Home", path: "/admin/dashboard" },
            { label: "Reports", path: "/admin/reports" },
          ]}
        />

        <div className="flex items-start gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary-100 text-primary-600 dark:bg-primary-900/40 dark:text-primary-300">
            <BarChart3 size={26} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Reports</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Select a report type and optional filters, then run.
            </p>
          </div>
        </div>

        {/* Filters */}
        <Card className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 items-end">
            <FilterSelect
              variant="boxed"
              label="Report type"
              value={reportType}
              onChange={(val) => setReportType(val as AdminReportType)}
              allLabel="Select report type"
              options={REPORT_TYPE_OPTIONS}
            />
            <Input
              label="Date from"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
            <Input
              label="Date to"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
            <Button onClick={() => runReport(1)} isLoading={loading}>
              Run report
            </Button>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <FilterSelect
              variant="boxed"
              label="Status (optional)"
              value={status}
              onChange={setStatus}
              allLabel="All statuses"
              options={STATUS_OPTIONS[reportType]}
            />
            <Input
              label="Search (optional)"
              placeholder="Name, email, identifier…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="border-t border-gray-100 pt-4 dark:border-gray-700">
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Download the same report as CSV or Excel (uses the filters above).
              </p>
              <Dropdown
                value={exportFormat}
                options={[
                  { label: "CSV", value: "csv" },
                  { label: "Excel", value: "excel" },
                ]}
                onChange={(val) => setExportFormat(val as "csv" | "excel")}
              />
              <Button variant="outline" onClick={handleExport} isLoading={exporting} disabled={exporting}>
                <Download size={16} className="mr-1.5" />
                Download export
              </Button>
            </div>
          </div>
        </Card>

        {/* Results */}
        {!hasRun ? (
          <Card>
            <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              Choose a report type and click "Run report" to load data.
            </p>
          </Card>
        ) : (
          <>
            {summary && (
              <Card>
                <h2 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Summary
                </h2>
                <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
                  {SUMMARY_CARDS[reportType].map((item) => (
                    <div key={item.key}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {item.label}
                      </p>
                      <p className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">
                        {formatSummaryValue(summary[item.key], item.format)}
                      </p>
                    </div>
                  ))}
                </div>
              </Card>
            )}

            <Card>
              <TableV2
                columns={columns}
                data={rows}
                pagination={pagination as any}
                onPageChange={(nextPage) => runReport(nextPage)}
                loading={loading}
              />
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default AdminReportsPage;
